import React, { useState, useEffect, useRef } from 'react';
import { AiOutlineMenu, AiOutlineQuestionCircle } from 'react-icons/ai';
import Navigation from './Navigation';
import BugReportModal from './BugReportModal';

/**
 * Sidebar component for the application navigation
 * 
 * @param {boolean} collapsed - Whether the sidebar is collapsed
 * @param {function} toggleSidebar - Function to toggle sidebar collapsed state
 * @param {function} onHoverLeave - Function to call when mouse leaves the sidebar
 * @param {function} onExpandChange - Function to call when expansion state changes
 */
const Sidebar = ({ 
  collapsed, 
  toggleSidebar,
  onHoverLeave,
  onExpandChange
}) => {
  // Track whether sidebar was manually toggled
  const [manuallyToggled, setManuallyToggled] = useState(false);
  // Track hover state internally
  const [isHoveringInternal, setIsHoveringInternal] = useState(false);
  // Timer ref for the hover delay
  const hoverTimerRef = useRef(null);
  // Track if the sidebar should be expanded due to hover (after delay)
  const [expandOnHover, setExpandOnHover] = useState(false);
  // State for the bug report modal
  const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);

  // Determine if sidebar should be expanded based on collapsed state or hover
  const isExpanded = !collapsed || expandOnHover;

  // Notify parent component about expansion state changes
  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(isExpanded);
    }
  }, [isExpanded, onExpandChange]);

  // Increased sidebar widths by 10%
  const collapsedWidth = "w-[70px]"; // was w-16 (64px), now ~70px
  const expandedWidth = "w-[282px]"; // was w-64 (256px), now ~282px

  // When collapsed state changes from parent, update our internal state
  useEffect(() => {
    // If sidebar is explicitly expanded by parent, treat as manually toggled
    if (!collapsed) {
      setManuallyToggled(true);
    }
  }, [collapsed]);

  // Handle hover effect with delay
  useEffect(() => {
    // Clear any existing timers
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    if (isHoveringInternal && collapsed && !manuallyToggled) {
      // Start a new timer to expand the sidebar after delay
      hoverTimerRef.current = setTimeout(() => {
        setExpandOnHover(true);
      }, 300); // 300ms delay before expanding
    } else {
      // Immediately collapse if not hovering or if manually toggled
      setExpandOnHover(false);
    }

    return () => {
      // Clean up timer on unmount or state change
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [isHoveringInternal, collapsed, manuallyToggled]);

  // Custom toggle function that sets manually toggled state
  const handleToggle = () => {
    const newCollapsedState = !collapsed;
    setManuallyToggled(newCollapsedState === false); // Track if we manually expanded
    toggleSidebar(); // Call the parent toggle function
  };

  // Handle mouse enter for non-hamburger area
  const handleMouseEnter = () => {
    setIsHoveringInternal(true);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsHoveringInternal(false);
    setExpandOnHover(false);
    
    // If we manually expanded, don't collapse when mouse leaves
    if (!manuallyToggled) {
      onHoverLeave();
    }
  };

  // Handle help button click
  const handleHelpClick = () => {
    setIsBugReportModalOpen(true);
  };

  return (
    <>
      <div 
        className={`transition-all duration-300 ease-in-out h-screen bg-sidebar z-10 ${
          isExpanded ? expandedWidth : collapsedWidth
        } relative flex flex-col`}
        onMouseLeave={handleMouseLeave}
      >
        {/* Fixed position hamburger button container with the same width as collapsed sidebar */}
        <div 
          className="h-[64px] relative z-20"
          // Prevent hover behavior on hamburger area
          onMouseEnter={(e) => e.stopPropagation()}
        >
          {/* Position hamburger exactly where it would be in collapsed state */}
          <div className={`absolute left-0 top-0 ${collapsedWidth} h-[64px] flex justify-center items-center`}>
            <button 
              onClick={handleToggle}
              className="hamburger-btn"
              aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              <AiOutlineMenu className="w-5 h-5" /> {/* Reduced icon size to ~66% */}
            </button>
          </div>
        </div>
        
        {/* Add spacing to prevent overlap with hamburger button */}
        <div className="pt-2"></div>
        
        {/* Sidebar Navigation - with mouse enter event on this section only */}
        <div 
          className="flex-grow"
          onMouseEnter={handleMouseEnter}
        >
          <Navigation isExpanded={isExpanded} collapsedWidth={collapsedWidth} />
        </div>
        
        {/* Help button at the bottom - styled the same as navigation items */}
        <div 
          className="mb-2 px-0"
          onMouseEnter={handleMouseEnter}
        >
          <button 
            className={`
              sidebar-item
              hover:bg-gray-200
              flex flex-row items-center relative h-14 mx-2 rounded-xl
              w-[calc(100%-16px)] 
            `}
            title="Help"
            onClick={handleHelpClick}
          >
            {/* Fixed width container for icon - positioned exactly like the hamburger */}
            <div className={`${collapsedWidth} flex-shrink-0 flex justify-center items-center -ml-2`}>
              <div className="text-gray-600">
                <AiOutlineQuestionCircle className="w-5 h-5" />
              </div>
            </div>
            
            {/* Help text with fade-in transition */}
            <div 
              className={`
                overflow-hidden
                ${isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 pointer-events-none'}
              `}
              style={{ 
                transitionProperty: 'opacity, width',
                transitionDuration: '300ms',
                transitionTimingFunction: 'ease-in-out',
                transitionDelay: isExpanded ? '150ms' : '0ms'
              }}
            >
              <span className="text-base whitespace-nowrap font-normal">Help</span>
            </div>
          </button>
        </div>
      </div>
      
      {/* Bug Report Modal */}
      <BugReportModal 
        isOpen={isBugReportModalOpen}
        onClose={() => setIsBugReportModalOpen(false)}
      />
    </>
  );
};

export default Sidebar; 